<template>
  <div class="prov-wrap">
    <!-- Header -->
    <div class="prov-header">
      <div>
        <h5 class="prov-title">CF Enterprise Bulk Automation</h5>
        <p class="prov-subtitle">Queue pending CF Enterprise tasks into an automation batch.</p>
      </div>
      <button class="btn btn-primary prov-create-btn" :disabled="batchCreating" @click="initiateBulkEnterprise">
        <i :class="batchCreating ? 'fa fa-spinner fa-spin' : 'fa fa-bolt'" class="mr-1"></i>
        {{ batchCreating ? 'Creating Batch…' : 'New Batch' }}
      </button>
    </div>

    <!-- Toolbar: search + tabs -->
    <div class="prov-toolbar">
      <ul class="nav nav-tabs prov-tabs">
        <li class="nav-item" v-for="tab in statusTabs" :key="tab.key">
          <a href="#" class="nav-link" :class="{ active: statusFilter === tab.key }" @click.prevent="setStatusFilter(tab.key)">
            {{ tab.label }}
          </a>
        </li>
      </ul>
      <div class="prov-search">
        <i class="fa fa-search prov-search-icon"></i>
        <input v-model="searchQuery" type="text" class="form-control form-control-sm prov-search-input" placeholder="Search by domain…" />
        <button v-if="searchQuery" @click="searchQuery = ''" class="prov-search-clear">×</button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loadingBatches && batches.length === 0" class="prov-empty">
      <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
    </div>

    <!-- Empty -->
    <div v-else-if="displayBatches.length === 0" class="prov-empty">
      <i class="fa fa-inbox fa-2x mb-2 text-muted"></i>
      <div v-if="searchQuery">No batches found containing "<strong>{{ searchQuery }}</strong>"</div>
      <div v-else>No {{ statusTabs.find(t => t.key === statusFilter).label.toLowerCase() }} batches</div>
    </div>

    <!-- Batch table -->
    <div v-else>
      <table class="prov-table">
        <thead>
          <tr>
            <th>Batch ID</th>
            <th>Status</th>
            <th>Domains</th>
            <th>Created By</th>
            <th>Created At</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="batch in displayBatches" :key="batch.id">
            <td class="prov-td-id">{{ batch.batchId }}</td>
            <td>
              <span :style="batchStatusStyle(batch.status)" class="prov-status-pill">
                {{ batchStatusLabel(batch.status) }}
              </span>
            </td>
            <td>{{ batch.totalCount }}</td>
            <td>
              <div v-if="batch.createdBy" style="display:flex;align-items:center;gap:7px;">
                <img
                  :src="`https://www.gravatar.com/avatar/${emailHashGravatar(batch.createdByEmail)}?s=60&d=identicon`"
                  width="24" height="24"
                  class="rounded-circle"
                  style="border:1.5px solid #dee2e6;flex-shrink:0;"
                />
                <span>{{ batch.createdBy }}</span>
              </div>
              <span v-else class="text-muted">—</span>
            </td>
            <td>{{ formatDate(batch.createdAt) }}</td>
            <td class="text-right">
              <button class="btn btn-sm prov-view-btn" @click="$router.push({ name: 'cfEnterpriseBatchDetail', params: { batchId: batch.batchId } })">
                <i class="fa fa-list mr-1"></i> View
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="prov-pagination" v-if="currentPage > 1 || hasNextPage">
        <small class="text-muted">Page {{ currentPage }}</small>
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: currentPage === 1 || loadingBatches }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">&laquo; Prev</a>
          </li>
          <li class="page-item disabled"><span class="page-link">{{ currentPage }}</span></li>
          <li class="page-item" :class="{ disabled: !hasNextPage || loadingBatches }">
            <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">Next &raquo;</a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import {db} from '@/firebase/init';
import swal from 'sweetalert2';
import md5 from 'crypto-js/md5';

export default {
  name: 'CFProvisioning',
  data() {
    return {
      batchCreating: false,
      loadingBatches: false,
      batches: [],
      allBatches: [],
      currentPage: 1,
      pageSize: 10,
      hasNextPage: false,
      unsubscribe: null,
      statusFilter: 'pending',
      searchQuery: '',
      statusTabs: [
        {
          key: 'pending', label: 'Pending', icon: 'fa fa-clock-o',
          activeStyle: 'background:#fff8e1;color:#f57f17;border-color:#f9a825;',
          inactiveStyle: 'background:#fff;color:#999;border-color:#e0e0e0;'
        },
        {
          key: 'inProgress', label: 'In Progress', icon: 'fa fa-spinner fa-spin',
          activeStyle: 'background:#e3f2fd;color:#1565c0;border-color:#90caf9;',
          inactiveStyle: 'background:#fff;color:#999;border-color:#e0e0e0;'
        },
        {
          key: 'completed', label: 'Completed', icon: 'fa fa-check-circle',
          activeStyle: 'background:#e8f5e9;color:#2e7d32;border-color:#a5d6a7;',
          inactiveStyle: 'background:#fff;color:#999;border-color:#e0e0e0;'
        },
        {
          key: 'failed', label: 'Failed', icon: 'fa fa-times-circle',
          activeStyle: 'background:#fdecea;color:#c62828;border-color:#ef9a9a;',
          inactiveStyle: 'background:#fff;color:#999;border-color:#e0e0e0;'
        }
      ]
    };
  },
  mounted() {
    this.listenBatches();
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    displayBatches() {
      if (!this.searchQuery) return this.batches;
      const q = this.searchQuery.toLowerCase().trim();
      return this.allBatches.filter(b =>
        b.domains && b.domains.some(d => d.domain && d.domain.toLowerCase().includes(q))
      );
    },
    hasActiveBatch() {
      const done = ['complete', 'completed', 'failed', 'fail'];
      return this.batches.some(b => !done.includes(b.status));
    }
  },
  methods: {
    listenBatches() {
      if (this.unsubscribe) this.unsubscribe();
      this.loadingBatches = true;
      const statusValues = {
        pending: ['pending'],
        inProgress: ['running', 'in-progress', 'inProgress'],
        completed: ['complete', 'completed', 'success'],
        failed: ['failed', 'fail']
      };
      let query = db.collection('cfEnterpriseBatches')
        .where('status', 'in', statusValues[this.statusFilter]);
      query = query.limit(500);
      this.unsubscribe = query.onSnapshot(snapshot => {
        const sorted = snapshot.docs.slice().sort((a, b) => {
          const aT = a.data().createdAt && a.data().createdAt.toDate ? a.data().createdAt.toDate() : new Date(a.data().createdAt || 0);
          const bT = b.data().createdAt && b.data().createdAt.toDate ? b.data().createdAt.toDate() : new Date(b.data().createdAt || 0);
          return bT - aT;
        });
        this.allBatches = sorted.map(doc => ({ id: doc.id, ...doc.data() }));
        this.applyPage();
        this.loadingBatches = false;
      }, err => {
        console.error('listenBatches error', err);
        this.loadingBatches = false;
      });
    },
    applyPage() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      this.batches = this.allBatches.slice(start, end);
      this.hasNextPage = this.allBatches.length > end;
    },
    setStatusFilter(key) {
      if (this.statusFilter === key) return;
      this.statusFilter = key;
      this.currentPage = 1;
      this.listenBatches();
    },
    goToPage(p) {
      if (p === this.currentPage) return;
      if (p < 1) return;
      if (p > this.currentPage && !this.hasNextPage) return;
      this.currentPage = p;
      this.applyPage();
    },
    statusBadge(status) {
      return {
        'badge-warning': status === 'pending',
        'badge-info': status === 'running' || status === 'in-progress' || status === 'inProgress',
        'badge-success': status === 'complete' || status === 'completed',
        'badge-danger': status === 'failed' || status === 'fail'
      };
    },
    statusLabel(status) {
      const map = {
        'pending': 'Pending',
        'running': 'Running',
        'in-progress': 'In Progress',
        'inProgress': 'In Progress',
        'complete': 'Complete',
        'completed': 'Complete',
        'failed': 'Failed',
        'fail': 'Failed'
      };
      return map[status] || status;
    },
    batchStatusLabel(status) {
      const map = {
        pending: 'Pending', running: 'In Progress', 'in-progress': 'In Progress',
        inProgress: 'In Progress', complete: 'Completed', completed: 'Completed',
        success: 'Completed', failed: 'Failed', fail: 'Failed'
      };
      return map[status] || status || 'Pending';
    },
    batchStatusStyle(status) {
      if (status === 'complete' || status === 'completed' || status === 'success')
        return { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' };
      if (status === 'failed' || status === 'fail')
        return { background: '#fdecea', color: '#c62828', border: '1px solid #ef9a9a' };
      if (status === 'running' || status === 'in-progress' || status === 'inProgress')
        return { background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9' };
      return { background: '#f5f5f5', color: '#757575', border: '1px solid #e0e0e0' };
    },
    domainCounts(batch) {
      const counts = { success: 0, failed: 0, inProgress: 0, pending: 0, followUp: 0 };
      if (!batch.domains) return counts;
      batch.domains.forEach(function(d) {
        const s = d.status || '';
        if (s === 'follow-up') { counts.followUp++; return; }
        if (s === 'pass' || s === 'complete' || s === 'completed' || s === 'success') counts.success++;
        else if (s === 'fail' || s === 'failed' || s === 'error') counts.failed++;
        else if (s === 'running' || s === 'in-progress' || s === 'inProgress') counts.inProgress++;
        else counts.pending++;
      });
      return counts;
    },
    emailHashGravatar(email) {
      if (!email) return null;
      return md5(email.trim().toLowerCase()).toString();
    },
    formatDate(val) {
      if (!val) return '—';
      const d = val.toDate ? val.toDate() : new Date(val);
      return d.toLocaleString();
    },
    async initiateBulkEnterprise() {
      const result = await swal({
        title: 'Bulk CF Enterprise Automation',
        html: `
          <p class="mb-2">How many pending tasks do you want to automate?</p>
          <input id="swal-batch-count" type="number" min="1" max="100" value="5"
            class="swal2-input" style="width:120px;text-align:center;" />
        `,
        showCancelButton: true,
        confirmButtonText: 'Create Batch',
        preConfirm: () => {
          const val = parseInt(document.getElementById('swal-batch-count').value, 10);
          if (!val || val < 1 || val > 100) {
            swal.showValidationMessage('Please enter a number between 1 and 100');
            return false;
          }
          return val;
        }
      });

      const count = result.value;
      if (!count) return;

      try {
        this.batchCreating = true;

        const domains = [];
        let lastDoc = null;

        while (domains.length < count) {
          let query = db.collection('domains')
            .where('cfEnterprise.status', '==', 'pending')
            .limit(count);
          if (lastDoc) query = query.startAfter(lastDoc);

          const snapshot = await query.get();
          if (snapshot.empty) break;

          for (let i = 0; i < snapshot.docs.length; i++) {
            if (domains.length >= count) break;
            const doc = snapshot.docs[i];
            const data = doc.data();
            const cfEnt = data.cfEnterprise || {};
            if (cfEnt.checkinBy) continue;
            if (cfEnt.followUp === true) continue;
            domains.push({
              ip: data.ip || '',
              id: doc.id,
              uid: data.uid || '',
              domain: data.domain || '',
              status: 'pending'
            });
          }

          lastDoc = snapshot.docs[snapshot.docs.length - 1];
          if (snapshot.docs.length < count) break;
        }

        if (!domains.length) {
          return swal('No Pending Tasks', 'There are no pending CF Enterprise tasks to queue.', 'info');
        }
        console.log(domains)
        // return
        const batchRef = db.collection('cfEnterpriseBatches').doc();
        await batchRef.set({
          batchId: batchRef.id,
          status: 'pending',
          createdAt: new Date(),
          totalCount: domains.length,
          createdBy: `${this.user.firstName} ${this.user.lastName}`,
          createdByEmail: this.user.email,
          domains
        });

        await this.$http.post('zone/batch-automate/run', {
          grant_type: 'token',
          batchId: batchRef.id
        });
      } catch (e) {
        console.error('initiateBulkEnterprise error', e);
        swal('Error', 'Something went wrong while creating the batch.', 'error');
      } finally {
        this.batchCreating = false;
      }
    }
  }
};
</script>
<style scoped>
/* Layout */
.prov-wrap { padding: 20px 24px; }

.prov-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}
.prov-title { font-size: 16px; font-weight: 700; margin: 0 0 2px; color: #1a1a2e; }
.prov-subtitle { font-size: 12px; color: #888; margin: 0; }
.prov-create-btn { white-space: nowrap; font-size: 13px; }

/* Toolbar */
.prov-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.prov-tabs { border-bottom: none; flex-shrink: 0; }
.prov-tabs .nav-link {
  font-size: 13px;
  padding: 6px 14px;
  border: 1px solid transparent !important;
  border-radius: 6px 6px 0 0;
  color: #666;
}
.prov-tabs .nav-link.active {
  color: #5b7cfb;
  background: #fff;
  border-color: #dee2e6 #dee2e6 #fff !important;
  font-weight: 600;
}
.prov-tabs .nav-link:before,
.prov-tabs .nav-link:after,
.prov-tabs .nav-link.active:before,
.prov-tabs .nav-link.active:after {
  display: none !important;
  border: 0 !important;
  content: none !important;
}
.prov-tabs { border-bottom: 1px solid #dee2e6; }

.prov-search { position: relative; min-width: 220px; }
.prov-search-icon { position: absolute; left: 9px; top: 50%; transform: translateY(-50%); color: #aaa; font-size: 12px; }
.prov-search-input { padding-left: 28px !important; border-radius: 6px; }
.prov-search-clear { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #aaa; cursor: pointer; padding: 0; font-size: 16px; line-height: 1; }

/* Empty state */
.prov-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px 24px; color: #aaa; text-align: center; font-size: 13px; gap: 8px; }

/* Batch table */
.prov-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.prov-table thead tr {
  border-bottom: 2px solid #e9ecef;
}
.prov-table th {
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #888;
  text-align: left;
  white-space: nowrap;
}
.prov-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #f0f2f4;
  color: #444;
  vertical-align: middle;
}
.prov-table tbody tr:hover td { background: #f9fafe; }
.prov-table tbody tr:last-child td { border-bottom: none; }

.prov-status-pill { border-radius: 99px; padding: 3px 10px; font-size: 11px; font-weight: 600; display: inline-block; white-space: nowrap; }
.prov-td-id { font-family: monospace; font-size: 11px; color: #999; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.prov-view-btn {
  border: 1.5px solid #dee2e6;
  background: #f8f9fa;
  color: #555;
  font-size: 12px;
  border-radius: 6px;
  padding: 4px 12px;
  white-space: nowrap;
  transition: all .15s;
}
.prov-view-btn:hover { border-color: #5b7cfb; color: #5b7cfb; background: #f0f3ff; }

/* Pagination */
.prov-pagination { display: flex; align-items: center; justify-content: space-between; padding: 12px 4px 0; }
</style>
